<template>
  <section
    class="accruals-estimation-rules-editor"
    data-testid="accruals-configuration-page__estimation-rules-editor"
  >
    <accruals-estimation-rules-list
      :rules="rules"
      :selected-rule-id="selectedRuleId"
      :read-only="readOnly"
      @select="onSelectRule"
      @add-rule="onAddRule"
      @reorder="onReorderRules"
    />

    <accruals-estimation-rule-panel
      v-if="selectedRule"
      :rule="selectedRule"
      :saving="saving"
      :read-only="readOnly"
      :condition-errors="conditionErrors"
      :templates="templates"
      @update-rule="onUpdateRule"
      @add-group="onAddGroup"
      @remove-group="onRemoveGroup"
      @set-group-logic="onSetGroupLogic"
      @set-group-connector="onSetGroupConnector"
      @add-condition="onAddCondition"
      @update-condition="onUpdateCondition"
      @delete-condition="onDeleteCondition"
      @cancel="onCancel"
      @save="onSave"
      @delete-rule="onDeleteRule"
    />
  </section>
</template>

<script>
import AccrualsEstimationRulesList from './AccrualsEstimationRulesList.vue';
import AccrualsEstimationRulePanel from './AccrualsEstimationRulePanel.vue';
import { createEmptyEstimationRule } from '../accrualsEstimationRulesConstants';
import { estimationRuleId, normalizeEstimationRule, uniqueEstimationRules } from '../accrualsEstimationRuleUtils';
import { estimationGroupsPatch } from '../accrualsEstimationGroupsUtils';
import {
  createEmptyCondition,
  createEmptyImGroup,
  IM_GROUP_LOGIC,
} from '../accrualsConfigurationConstants';
import {
  CONDITIONS_INCOMPLETE_MESSAGE,
  validateCondition,
  validateImGroups,
} from '../accrualsConditionValidation';

export default {
  name: 'AccrualsEstimationRulesEditor',
  components: {
    AccrualsEstimationRulesList,
    AccrualsEstimationRulePanel,
  },
  props: {
    rules: { type: Array, default: () => [] },
    selectedRuleId: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
    templates: { type: Array, default: () => [] },
  },
  data() {
    return {
      conditionErrors: {},
      draftsById: {},
    };
  },
  computed: {
    selectedRule() {
      const id = this.selectedRuleId;
      if (id && this.draftsById[id]) return this.draftsById[id];
      return (this.rules || []).find((rule) => estimationRuleId(rule) === id) || null;
    },
  },
  watch: {
    selectedRuleId() {
      this.conditionErrors = {};
    },
  },
  methods: {
    onSelectRule(ruleId) {
      this.$emit('select-rule', ruleId);
    },
    onAddRule() {
      if (this.readOnly) return;
      const nextRule = createEmptyEstimationRule({
        id: `__draft__${Date.now()}`,
        name: 'New rule',
      });
      this.$emit('add-rule', nextRule);
      this.$set(this.draftsById, estimationRuleId(nextRule), { ...nextRule });
      this.conditionErrors = {};
    },
    onReorderRules(nextRules) {
      if (this.readOnly) return;
      this.$emit('update-rules', nextRules);
    },
    onUpdateRule(patch) {
      if (this.readOnly) return;
      const base = this.selectedRule;
      if (!base) return;
      const nextRule = normalizeEstimationRule({ ...base, ...patch });
      const id = estimationRuleId(nextRule);
      if (id) this.$set(this.draftsById, id, nextRule);
      this.$emit('update-rule', nextRule);
    },
    currentGroups() {
      return (this.selectedRule?.groups || []).slice();
    },
    applyGroupsPatch(groups, groupConnectors, groupLogic) {
      this.onUpdateRule(estimationGroupsPatch(
        groups,
        groupConnectors || this.selectedRule?.groupConnectors,
        groupLogic || this.selectedRule?.groupLogic,
      ));
    },
    onAddGroup() {
      const groups = this.currentGroups();
      groups.push(createEmptyImGroup(groups.length));
      const connectors = (this.selectedRule?.groupConnectors || []).slice();
      connectors.push(this.selectedRule?.groupLogic || IM_GROUP_LOGIC.AND);
      this.applyGroupsPatch(groups, connectors);
      this.conditionErrors = {};
    },
    onRemoveGroup(groupId) {
      const groups = this.currentGroups();
      if (groups.length <= 1) return;
      this.applyGroupsPatch(groups.filter((group) => group.id !== groupId));
      this.conditionErrors = {};
    },
    onSetGroupLogic({ groupId, logic }) {
      const groups = this.currentGroups().map((group) => (
        group.id === groupId
          ? { ...group, logic: logic === IM_GROUP_LOGIC.OR ? IM_GROUP_LOGIC.OR : IM_GROUP_LOGIC.AND }
          : group
      ));
      this.applyGroupsPatch(groups);
    },
    onSetGroupConnector(payload) {
      const index = typeof payload === 'object' ? payload.index : 0;
      const logic = typeof payload === 'object' ? payload.logic : payload;
      const synced = estimationGroupsPatch(
        this.currentGroups(),
        this.selectedRule?.groupConnectors,
        this.selectedRule?.groupLogic,
      );
      const connectors = synced.groupConnectors.slice();
      if (index < 0 || index >= connectors.length) return;
      connectors.splice(index, 1, logic === IM_GROUP_LOGIC.OR ? IM_GROUP_LOGIC.OR : IM_GROUP_LOGIC.AND);
      this.applyGroupsPatch(this.currentGroups(), connectors);
    },
    onAddCondition(groupId) {
      const groups = this.currentGroups().map((group) => {
        if (group.id !== groupId) return group;
        const conditions = group.conditions.slice();
        conditions.push(createEmptyCondition(conditions.length));
        return { ...group, conditions };
      });
      this.applyGroupsPatch(groups);
      this.conditionErrors = {};
    },
    onUpdateCondition({ groupId, index, condition }) {
      const groups = this.currentGroups().map((group) => {
        if (group.id !== groupId) return group;
        const conditions = group.conditions.slice();
        conditions.splice(index, 1, condition);
        return { ...group, conditions };
      });
      this.applyGroupsPatch(groups);
      const key = `${groupId}:${index}`;
      if (this.conditionErrors[key]) {
        const { errors } = validateCondition(condition);
        this.$set(this.conditionErrors, key, errors);
      }
    },
    onDeleteCondition({ groupId, index }) {
      const group = this.currentGroups().find((item) => item.id === groupId);
      if (!group) return;
      const nextConditions = group.conditions.slice();
      nextConditions.splice(index, 1);

      let groups;
      if (!nextConditions.length) {
        if (this.currentGroups().length <= 1) {
          groups = this.currentGroups().map((item) => (
            item.id === groupId
              ? { ...item, conditions: [createEmptyCondition(0)] }
              : item
          ));
        } else {
          groups = this.currentGroups().filter((item) => item.id !== groupId);
        }
      } else {
        groups = this.currentGroups().map((item) => (
          item.id === groupId ? { ...item, conditions: nextConditions } : item
        ));
      }

      this.applyGroupsPatch(groups);
      this.conditionErrors = {};
    },
    collectConditionErrors() {
      const { valid, errorsByKey } = validateImGroups(this.selectedRule?.groups || []);
      this.conditionErrors = errorsByKey;
      return valid;
    },
    onCancel() {
      const id = this.selectedRuleId;
      if (id && this.draftsById[id]) this.$delete(this.draftsById, id);
      this.conditionErrors = {};
      this.$emit('cancel-edit');
    },
    dirtyRules() {
      const drafts = Object.values(this.draftsById || {});
      if (drafts.length) {
        return uniqueEstimationRules(drafts).sort(
          (a, b) => Number(a.execution_sequence || 0) - Number(b.execution_sequence || 0),
        );
      }
      return this.selectedRule ? [this.selectedRule] : [];
    },
    onSave() {
      const rulesToSave = this.dirtyRules();
      for (let index = 0; index < rulesToSave.length; index += 1) {
        const rule = rulesToSave[index];
        const { valid, errorsByKey } = validateImGroups(rule?.groups || []);
        if (valid) continue;
        const id = estimationRuleId(rule);
        if (id) this.$emit('select-rule', id);
        this.conditionErrors = errorsByKey;
        this.$store.commit('sbError', CONDITIONS_INCOMPLETE_MESSAGE, { root: true });
        return;
      }
      const normalized = rulesToSave.map((rule) => normalizeEstimationRule(rule));
      this.conditionErrors = {};
      console.log('[AccrualsEstimationRulesEditor]: save-rules', {
        count: normalized.length,
        ids: normalized.map((rule) => estimationRuleId(rule)),
      });
      this.$emit('save-rules', normalized);
      this.draftsById = {};
    },
    onDeleteRule() {
      if (!this.selectedRule) return;
      const id = estimationRuleId(this.selectedRule);
      if (id && this.draftsById[id]) this.$delete(this.draftsById, id);
      this.$emit('delete-rule', id);
      this.conditionErrors = {};
    },
  },
  beforeDestroy() {
    this.draftsById = {};
    this.conditionErrors = {};
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimationRulesEditor.scss';
</style>
